/*
  Bundle builder: pick quantities from a collection, watch the discount tiers fill up, then add
  everything to the cart in one go. Items go into the cart as normal individual products; the
  discount itself comes from an automatic discount in Shopify admin that matches the tiers.
*/
if (!customElements.get('m-bundle-builder')) {
  const money = (cents) => {
    const format =
      (window.DeliSettings && (DeliSettings.money_format || DeliSettings.moneyFormat)) || '';
    if (typeof window.formatMoney === 'function' && format) return formatMoney(cents, format);
    return (cents / 100).toFixed(2);
  };

  class MBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.storageKey = this.dataset.storageKey || 'm-bundle';
      this.addLabel = this.dataset.addLabel || 'Add bundle to cart';
      this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
      this.itemsEl = this.querySelector('[data-bundle-items]');
      this.emptyEl = this.querySelector('[data-bundle-empty]');
      this.totalsEl = this.querySelector('[data-bundle-totals]');
      this.addButtons = Array.from(this.querySelectorAll('[data-bundle-add]'));
      this.errorEl = this.querySelector('[data-bundle-error]');
      this.tiers = this.readTiers();
      this.state = new Map();

      this.cards.forEach((card) => {
        card.dataset.currentId = card.dataset.variantId || '';
      });

      this.summary = this.querySelector('[data-bundle-summary]');
      this.spacer = this.querySelector('[data-bundle-spacer]');
      this.mobileQuery = window.matchMedia('(max-width: 1023px)');

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.close();
      };
      document.addEventListener('keydown', this.onKeydown);
      this.watchVisibility();
      this.restore();
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.sectionObserver) this.sectionObserver.disconnect();
      if (this.summaryObserver) this.summaryObserver.disconnect();
      document.documentElement.classList.remove('m-bundle-lock');
    }

    /* ---------- mobile pill + bottom sheet ---------- */

    // The pill shows while the section is on screen, or anywhere on the page once something
    // is picked; never while the summary itself is in view or open.
    watchVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.inView = true;
        return;
      }
      const section = this.closest('section') || this;
      this.sectionObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.updatePill();
      });
      this.sectionObserver.observe(section);

      if (this.summary) {
        this.summaryObserver = new IntersectionObserver(
          ([entry]) => {
            this.summaryInView = entry.isIntersecting;
            this.updatePill();
          },
          { threshold: 0.3 }
        );
        this.summaryObserver.observe(this.summary);
      }
    }

    updatePill() {
      const isOpen = this.classList.contains('is-open');
      const show = (this.inView || this.hasItems) && !(this.summaryInView && !isOpen) && !isOpen;
      this.classList.toggle('show-pill', Boolean(show));
    }

    open() {
      if (!this.mobileQuery.matches) {
        if (this.summary) this.summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      clearTimeout(this.closeTimer);
      this.classList.remove('is-closing');
      if (this.spacer && this.summary && !this.classList.contains('is-open')) {
        const marginTop = parseFloat(getComputedStyle(this.summary).marginTop) || 0;
        this.spacer.style.height = `${this.summary.offsetHeight + marginTop}px`;
      }
      this.classList.add('is-open');
      document.documentElement.classList.add('m-bundle-lock');
      this.updatePill();
    }

    close() {
      if (!this.classList.contains('is-open') || this.classList.contains('is-closing')) return;
      this.classList.add('is-closing');
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        this.classList.remove('is-open', 'is-closing');
        if (this.spacer) this.spacer.style.height = '';
        document.documentElement.classList.remove('m-bundle-lock');
        this.updatePill();
      }, 260);
    }

    readTiers() {
      const el = this.querySelector('[data-bundle-tiers]');
      if (!el) return [];
      try {
        return JSON.parse(el.textContent)
          .filter((tier) => tier && Number(tier.quantity) > 0)
          .sort((a, b) => a.quantity - b.quantity);
      } catch (error) {
        return [];
      }
    }

    /* ---------- state ---------- */

    cardData(card) {
      const select = card.querySelector('[data-bundle-variant]');
      const option = select && select.options[select.selectedIndex];
      return {
        id: String(option ? option.value : card.dataset.variantId || ''),
        price: Number(option ? option.dataset.price : card.dataset.price) || 0,
        title: card.dataset.title || '',
        variantTitle: option ? option.dataset.variantTitle || '' : '',
        image: card.dataset.image || '',
        url: card.dataset.url || '',
      };
    }

    quantityOf(id) {
      const entry = this.state.get(String(id));
      return entry ? entry.quantity : 0;
    }

    setQuantity(data, quantity) {
      const id = String(data.id);
      if (!id) return;
      if (quantity <= 0) {
        this.state.delete(id);
      } else {
        this.state.set(id, { ...data, id, quantity });
      }
      this.save();
      this.render();
    }

    save() {
      try {
        const entries = Array.from(this.state.values()).map((item) => ({
          id: item.id,
          quantity: item.quantity,
        }));
        sessionStorage.setItem(this.storageKey, JSON.stringify(entries));
      } catch (error) {
        /* storage unavailable: the bundle simply does not survive a reload */
      }
    }

    restore() {
      let entries = [];
      try {
        entries = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
      } catch (error) {
        return;
      }
      entries.forEach((entry) => {
        const card = this.cards.find((item) => this.variantIds(item).includes(String(entry.id)));
        if (!card) return;
        const select = card.querySelector('[data-bundle-variant]');
        if (select) select.value = String(entry.id);
        card.dataset.currentId = String(entry.id);
        const data = this.cardData(card);
        if (data.id !== String(entry.id)) return;
        this.state.set(data.id, { ...data, quantity: Number(entry.quantity) || 1 });
      });
    }

    variantIds(card) {
      const select = card.querySelector('[data-bundle-variant]');
      if (select) return Array.from(select.options).map((option) => String(option.value));
      return [String(card.dataset.variantId)];
    }

    /* ---------- events ---------- */

    onClick = (event) => {
      const step = event.target.closest('[data-bundle-plus], [data-bundle-minus]');
      if (step) {
        const card = step.closest('[data-bundle-card]');
        const data = this.cardData(card);
        const delta = step.hasAttribute('data-bundle-plus') ? 1 : -1;
        this.setQuantity(data, this.quantityOf(data.id) + delta);
        return;
      }

      const remove = event.target.closest('[data-bundle-remove]');
      if (remove) {
        this.setQuantity({ id: remove.dataset.id }, 0);
        return;
      }

      if (event.target.closest('[data-bundle-open]')) {
        this.open();
        return;
      }
      if (event.target.closest('[data-bundle-close]')) {
        this.close();
        return;
      }
      if (event.target.closest('[data-bundle-add]')) this.addToCart();
    };

    onChange = (event) => {
      const select = event.target.closest('[data-bundle-variant]');
      if (!select) return;
      const card = select.closest('[data-bundle-card]');
      const previousId = card.dataset.currentId;
      const quantity = this.quantityOf(previousId);
      if (previousId) this.state.delete(String(previousId));

      card.dataset.currentId = String(select.value);
      const data = this.cardData(card);
      const option = select.options[select.selectedIndex];
      const priceEl = card.querySelector('[data-bundle-card-price]');
      if (priceEl && option && option.dataset.priceFormatted) {
        priceEl.textContent = option.dataset.priceFormatted;
      }
      // Keep whatever was already chosen, now on the newly picked variant.
      this.setQuantity(data, quantity);
    };

    /* ---------- rendering ---------- */

    get totals() {
      let quantity = 0;
      let subtotal = 0;
      this.state.forEach((item) => {
        quantity += item.quantity;
        subtotal += item.quantity * item.price;
      });
      const tier = this.tiers.filter((entry) => quantity >= entry.quantity).pop() || null;
      const next = this.tiers.find((entry) => quantity < entry.quantity) || null;
      const percent = tier ? Number(tier.percent) || 0 : 0;
      const discount = Math.round((subtotal * percent) / 100);
      return { quantity, subtotal, tier, next, percent, discount, total: subtotal - discount };
    }

    render() {
      const { quantity, subtotal, tier, next, percent, discount, total } = this.totals;

      this.cards.forEach((card) => {
        const count = this.quantityOf(card.dataset.currentId);
        const countEl = card.querySelector('[data-bundle-count]');
        const badge = card.querySelector('[data-bundle-badge]');
        if (countEl) countEl.textContent = count;
        card.classList.toggle('is-picked', count > 0);
        if (badge) {
          badge.textContent = count;
          badge.hidden = count === 0;
        }
      });

      if (this.itemsEl) {
        this.itemsEl.innerHTML = Array.from(this.state.values())
          .map(
            (item) => `
              <li class="m-bundle__item">
                <span class="m-bundle__item-image">${
                  item.image ? `<img src="${item.image}" alt="" loading="lazy">` : ''
                }</span>
                <span class="m-bundle__item-info">
                  <span class="m-bundle__item-name">${item.title}</span>
                  ${item.variantTitle ? `<span class="m-bundle__item-variant">${item.variantTitle}</span>` : ''}
                </span>
                <span class="m-bundle__item-qty">&times;${item.quantity}</span>
                <span class="m-bundle__item-price">${money(item.price * item.quantity)}</span>
                <button type="button" class="m-bundle__item-remove" data-bundle-remove data-id="${item.id}" aria-label="Remove ${item.title}">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
                </button>
              </li>`
          )
          .join('');
      }

      if (this.emptyEl) this.emptyEl.hidden = quantity > 0;
      if (this.totalsEl) this.totalsEl.hidden = quantity === 0;
      this.classList.toggle('has-items', quantity > 0);

      this.setText('[data-bundle-subtotal]', money(subtotal));
      this.setText('[data-bundle-total]', money(total));
      const savingRow = this.querySelector('[data-bundle-saving-row]');
      if (savingRow) savingRow.hidden = discount === 0;
      this.setText('[data-bundle-saving]', `- ${money(discount)}`);
      if (percent) this.setText('[data-bundle-saving-label]', `${this.savingLabel} (${percent}%)`);

      this.renderProgress(quantity, tier, next, percent);

      this.addButtons.forEach((button) => {
        button.disabled = quantity === 0;
      });
      this.setText('[data-bundle-add-label]', quantity > 0 ? `${this.addLabel} (${quantity})` : this.addLabel);
      this.renderChips(quantity);
      this.renderPill(quantity, total, percent);
    }

    // Mobile tier chips above the grid: filled once that tier is reached.
    renderChips(quantity) {
      this.querySelectorAll('[data-chip-quantity]').forEach((chip) => {
        chip.classList.toggle('is-reached', quantity >= Number(chip.dataset.chipQuantity));
      });
    }

    renderPill(quantity, total, percent) {
      const count = this.querySelector('[data-bundle-pill-count]');
      const meta = this.querySelector('[data-bundle-pill-meta]');
      const save = this.querySelector('[data-bundle-pill-save]');
      if (count) {
        count.textContent = quantity;
        count.hidden = quantity === 0;
      }
      if (meta) {
        meta.textContent = quantity
          ? `${quantity} ${quantity === 1 ? 'item' : 'items'} · ${money(total)}`
          : 'Start your bundle';
      }
      if (save) {
        save.textContent = percent ? `-${percent}%` : '';
        save.hidden = !percent;
      }
      // Up to three photos of what is picked, the newest on top.
      const thumbs = this.querySelector('[data-bundle-pill-thumbs]');
      const icon = this.querySelector('[data-bundle-pill-icon]');
      const picked = Array.from(this.state.values()).filter((item) => item.image).reverse().slice(0, 3);
      if (thumbs) {
        thumbs.innerHTML = picked
          .map((item) => `<span class="m-bundle__pill-thumb"><img src="${item.image}" alt="" loading="lazy"></span>`)
          .join('');
        thumbs.dataset.count = picked.length;
      }
      if (icon) icon.hidden = picked.length > 0;

      const pill = this.querySelector('.m-bundle__pill');
      if (pill && quantity > (this.lastPillQuantity || 0)) {
        pill.classList.remove('is-bump');
        void pill.offsetWidth;
        pill.classList.add('is-bump');
      }
      this.lastPillQuantity = quantity;
      this.hasItems = quantity > 0;
      this.updatePill();
    }

    get savingLabel() {
      if (!this._savingLabel) {
        const el = this.querySelector('[data-bundle-saving-label]');
        this._savingLabel = (el && el.textContent.trim()) || 'Bundle discount';
      }
      return this._savingLabel;
    }

    renderProgress(quantity, tier, next, percent) {
      const top = this.tiers.length ? this.tiers[this.tiers.length - 1].quantity : 0;
      const ratio = top ? Math.min(100, (quantity / top) * 100) : 0;
      const progress = this.querySelector('[data-bundle-progress]');
      const fill = this.querySelector('[data-bundle-fill]');
      const fillMobile = this.querySelector('[data-bundle-fill-mobile]');
      if (fill) fill.style.width = `${ratio}%`;
      if (fillMobile) fillMobile.style.width = `${ratio}%`;

      if (progress) {
        // The gift icon rides along the bar, like the truck on the free shipping bar.
        progress.style.setProperty('--bundle-progress', `${ratio}%`);
        progress.classList.toggle('is-complete', Boolean(tier) && !next);
        progress.classList.toggle('is-started', quantity > 0);
        const track = progress.querySelector('[data-bundle-track]');
        if (track) track.setAttribute('aria-valuenow', Math.round(ratio));

        // A short pop whenever a new tier is reached.
        if (percent > (this.lastPercent || 0)) {
          progress.classList.add('is-unlocked');
          clearTimeout(this.unlockTimer);
          this.unlockTimer = setTimeout(() => progress.classList.remove('is-unlocked'), 900);
        }
        this.lastPercent = percent;
      }

      const ticks = this.querySelector('[data-bundle-ticks]');
      const marks = this.querySelector('[data-bundle-marks]');
      if (top) {
        const position = (entry) => Math.min(100, (entry.quantity / top) * 100);
        if (ticks) {
          ticks.innerHTML = this.tiers
            .map((entry) => {
              const left = position(entry);
              const edge = left >= 98 ? ' is-last' : left <= 2 ? ' is-first' : '';
              return `<span class="m-bundle__tick${
                quantity >= entry.quantity ? ' is-reached' : ''
              }${edge}" style="left:${left}%"><b>${entry.percent}% off</b><small>${entry.quantity}+</small></span>`;
            })
            .join('');
        }
        if (marks) {
          // Skip the notch at the very end of the bar: it would sit on the rounded edge.
          marks.innerHTML = this.tiers
            .filter((entry) => position(entry) > 1 && position(entry) < 98)
            .map(
              (entry) =>
                `<span class="m-bundle__mark${
                  quantity >= entry.quantity ? ' is-reached' : ''
                }" style="left:${position(entry)}%"></span>`
            )
            .join('');
        }
      }

      let status = '';
      if (!this.tiers.length) {
        status = quantity ? `${quantity} selected` : '';
      } else if (next) {
        const missing = next.quantity - quantity;
        const item = missing === 1 ? 'item' : 'items';
        status = percent
          ? `<b>${percent}% off</b> applied · add ${missing} more ${item} for <b>${next.percent}%</b>`
          : `Add <b>${missing}</b> more ${item} to unlock <b>${next.percent}% off</b>`;
      } else if (tier) {
        status = `Best price unlocked · <b>${percent}% off</b>`;
      }
      const statusEl = this.querySelector('[data-bundle-status]');
      if (statusEl) statusEl.innerHTML = status;

      this.setText(
        '[data-bundle-mobile-text]',
        quantity
          ? `${quantity} selected${percent ? ` · ${percent}% off` : ''}`
          : statusEl
          ? statusEl.textContent
          : 'Start your bundle'
      );
    }

    setText(selector, text) {
      this.querySelectorAll(selector).forEach((el) => {
        el.textContent = text;
      });
    }

    /* ---------- add to cart ---------- */

    async addToCart() {
      const items = Array.from(this.state.values()).map((item) => ({
        id: Number(item.id),
        quantity: item.quantity,
      }));
      if (!items.length || !this.addButtons.length) return;

      this.addButtons.forEach((button) => {
        button.disabled = true;
        button.classList.add('is-loading');
      });
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const response = await fetch(`${root.endsWith('/') ? root : `${root}/`}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add the bundle to the cart');
        }

        this.state.clear();
        this.save();
        this.close();
        this.render();
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      } catch (error) {
        console.error(error);
        if (this.errorEl) {
          this.errorEl.textContent = error.message;
          this.errorEl.hidden = false;
        }
        this.addButtons.forEach((button) => {
          button.disabled = false;
        });
      } finally {
        this.addButtons.forEach((button) => button.classList.remove('is-loading'));
      }
    }
  }

  customElements.define('m-bundle-builder', MBundleBuilder);
}
